<template>
  <div class="seo-article-page">
    <SiteHeader/>

    <article class="seo-article">
      <v-container class="pt-16 pb-4">
        <h1 class="seo-article-title reveal" style="--reveal-index: 0">{{ content.h1 }}</h1>
        <div v-if="content.author" class="seo-article-byline reveal" style="--reveal-index: 1">
          <span>{{ content.author }}</span>
          <span class="seo-article-byline-dot">&bull;</span>
          <span>{{ formattedDate }}</span>
        </div>
        <p class="seo-article-intro reveal" style="--reveal-index: 1">{{ content.intro }}</p>
      </v-container>

      <v-container class="pb-8 seo-article-body">
        <section v-for="(section, index) in content.sections" :key="`section-${index}`" class="seo-article-section reveal" :style="{ '--reveal-index': index % 4 }">
          <h2 class="seo-article-heading">{{ section.heading }}</h2>
          <img v-if="section.image" :src="section.image" :alt="section.imageAlt || section.heading" loading="lazy" class="seo-article-image"/>
          <div class="seo-article-text" v-html="section.body"></div>
        </section>
      </v-container>

      <v-container class="pb-8" v-if="$slots.afterSections">
        <slot name="afterSections"/>
      </v-container>

      <v-container class="pb-8 seo-article-body" v-if="faq && faq.length">
        <section class="seo-article-section seo-article-faq-section">
          <h2 class="seo-article-heading">Perguntas frequentes</h2>
          <div v-for="(item, index) in faq" :key="`faq-${index}`" class="seo-article-faq-item">
            <h3 class="seo-article-faq-question">{{ item.question }}</h3>
            <p class="seo-article-text">{{ item.answer }}</p>
          </div>
        </section>
      </v-container>

      <v-container class="pb-8" v-if="content.relatedLinks && content.relatedLinks.length">
        <h2 class="seo-article-heading">{{ content.relatedLinksTitle }}</h2>
        <div class="seo-internal-links-list mt-4">
          <a v-for="(link, index) in content.relatedLinks" :key="`related-${index}`" :href="link.href" class="seo-internal-link-card">
            {{ link.label }}
            <v-icon size="16" color="#0277BD">mdi-arrow-top-right-thin</v-icon>
          </a>
        </div>
      </v-container>

      <v-container class="pb-16 text-center">
        <a :href="content.pillarLink.href" class="seo-back-to-pillar">
          <v-icon size="16" color="#0277BD" class="mr-1">mdi-arrow-left</v-icon>
          {{ content.pillarLink.label }}
        </a>
      </v-container>
    </article>

    <SiteFooter/>
  </div>
</template>

<script>
import SiteHeader from "./SiteHeader.vue";
import SiteFooter from "./SiteFooter.vue";

export default {
  name: 'SeoBlogArticle',
  props: {
    content: { type: Object, required: true },
    faq: { type: Array, default: () => [] },
  },
  components: {
    SiteHeader,
    SiteFooter,
  },
  data: () => ({
    revealObserver: null,
  }),
  computed: {
    formattedDate: function() {
      if (!this.content.date) return ''
      const [year, month, day] = this.content.date.split('-')
      return `${day}/${month}/${year}`
    },
  },
  mounted: function() {
    this.$nextTick(() => this.setupRevealObserver())
  },
  beforeDestroy: function() {
    if (this.revealObserver) this.revealObserver.disconnect()
  },
  methods: {
    setupRevealObserver: function() {
      const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const elements = this.$el.querySelectorAll ? this.$el.querySelectorAll('.reveal') : []
      if (prefersReducedMotion || !window.IntersectionObserver) {
        elements.forEach(el => el.classList.add('is-visible'))
        return
      }
      this.revealObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            this.revealObserver.unobserve(entry.target)
          }
        })
      }, { threshold: 0.15 })
      elements.forEach(el => this.revealObserver.observe(el))
    },
  },
}
</script>
<style scoped>
.seo-article-page {
  font-family: 'Montserrat', sans-serif;
  background: #F7F5EF;
  padding-top: 72px;
}
.reveal {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.6s cubic-bezier(0.16,1,0.3,1), transform 0.6s cubic-bezier(0.16,1,0.3,1);
  transition-delay: calc(var(--reveal-index, 0) * 90ms);
}
.reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
}
@media (prefers-reduced-motion: reduce) {
  .reveal {
    transition: none !important;
    opacity: 1 !important;
    transform: none !important;
  }
}
.seo-article {
  max-width: 760px;
  margin: 0 auto;
}
.seo-article-title {
  font-weight: 900;
  font-size: 2.1rem;
  line-height: 1.25;
  color: #0277BD;
  letter-spacing: -0.01em;
}
.seo-article-byline {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: #8892A0;
  font-size: 0.85rem;
  margin-top: 0.75rem;
}
.seo-article-byline-dot {
  opacity: 0.6;
}
.seo-article-intro {
  color: #5B6472;
  font-size: 1.05rem;
  margin-top: 1rem;
  line-height: 1.6;
}
.seo-article-body {
  background: #FFFFFF;
  border-radius: 1.25rem;
  box-shadow: 0 8px 20px rgba(16,24,39,0.05);
}
.seo-article-section {
  padding: 1.25rem 0;
}
.seo-article-section:not(:last-child) {
  border-bottom: 1px solid rgba(16,24,39,0.06);
}
.seo-article-image {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 1rem;
  margin: 0.75rem 0 1.1rem;
  background: #F7F5EF;
}
.seo-article-heading {
  font-weight: 800;
  font-size: 1.2rem;
  color: #101827;
  margin: 0 0 0.6rem;
}
.seo-article-text {
  color: #5B6472;
  font-size: 0.98rem;
  line-height: 1.7;
}
.seo-article-text >>> a {
  color: #0277BD;
  font-weight: 700;
  text-decoration: none;
}
.seo-article-text >>> a:hover {
  text-decoration: underline;
}
.seo-article-text >>> code {
  overflow-wrap: anywhere;
  word-break: break-word;
}
.seo-article-faq-item:not(:last-child) {
  margin-bottom: 1.1rem;
}
.seo-article-faq-question {
  font-weight: 800;
  font-size: 1rem;
  color: #101827;
  margin: 0 0 0.35rem;
}
.seo-internal-links-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}
.seo-internal-link-card {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: #FFFFFF;
  border-radius: 999px;
  padding: 0.6rem 1.1rem;
  color: #0277BD;
  font-weight: 700;
  font-size: 0.85rem;
  text-decoration: none;
  box-shadow: 0 6px 16px rgba(16,24,39,0.05);
  transition: transform 0.2s ease;
}
.seo-internal-link-card:hover {
  transform: translateY(-2px);
}
.seo-back-to-pillar {
  display: inline-flex;
  align-items: center;
  color: #0277BD;
  font-weight: 700;
  text-decoration: none;
}
.seo-back-to-pillar:hover {
  text-decoration: underline;
}
</style>
